import {
  mapBooleanToYesNoString,
  mapPhoneDocumentToPageModel,
  mapPhonePageModelToRequest,
  mapUserToCorrespondenceLanguage,
  mapYesNoStringToBoolean,
} from "../shared/mappers";
import { defaults } from "./defaults";
import {
  AnnualFlightSpending,
  BasicAccountBenefitsModel,
  CompanyTravellersCount,
  PersonType,
} from "./types";
import {
  LanguageBackendType,
  LanguageFrontendType,
} from "../shared/components/language/types";
import {
  BasicAccountApplicationDocument,
  BasicAccountBenefitsDto,
  CorrespondenceLanguage,
  NaturalPersonDocument,
} from "../../api/commercial/generated-types";
import { Gender } from "../shared/types";

const resolveCorrespondenceLanguage = (
  languageFromPersonDocument: CorrespondenceLanguage | undefined,
  languageFromUI: LanguageFrontendType,
) => {
  if (languageFromPersonDocument) {
    return languageFromPersonDocument;
  }

  // we don't support Italian.
  if (languageFromUI === LanguageFrontendType.it) {
    return null;
  }

  return mapUserToCorrespondenceLanguage(languageFromUI);
};

const mapPersonDocumentToPageModel = (
  personDocument: NaturalPersonDocument,
  language: LanguageFrontendType,
): PersonType => {
  return {
    gender: personDocument.gender,
    firstName: personDocument.firstName || "",
    lastName: personDocument.lastName || "",
    mobile: mapPhoneDocumentToPageModel(personDocument.mobile),
    emailBusiness: personDocument.emailBusiness,
    correspondenceLanguage: resolveCorrespondenceLanguage(
      personDocument.correspondenceLanguage,
      language,
    ),
  };
};

const mapDocumentToPageModel = (
  document: BasicAccountApplicationDocument,
  language: LanguageFrontendType,
): BasicAccountBenefitsModel => {
  const loyaltyProgramContactDocument = document.loyaltyProgramContact;
  const loyaltyContactExists = loyaltyProgramContactDocument !== null;

  // initially there is no record of a partner plus program thus it will be null.
  const partnerPlusBenefitDocument = document.partnerPlusBenefit;
  const isThereExistingPartnerPlus = partnerPlusBenefitDocument !== null;

  return {
    hasCashOnCard:
      document.isCashWithdrawalPossible === null
        ? defaults.hasCashOnCard
        : mapBooleanToYesNoString(document.isCashWithdrawalPossible),
    hasLoyalties: mapBooleanToYesNoString(
      partnerPlusBenefitDocument &&
        partnerPlusBenefitDocument.username !== null,
    ),
    customizedCard: document.customizedCard,
    plusPartnerUsername: isThereExistingPartnerPlus
      ? partnerPlusBenefitDocument.username
      : defaults.plusPartnerUsername,
    isContactPersonSame:
      document.adminIsLoyaltyProgramContact === null
        ? defaults.isContactPersonSame
        : mapBooleanToYesNoString(document.adminIsLoyaltyProgramContact),
    contactPerson:
      loyaltyContactExists && !document.adminIsLoyaltyProgramContact
        ? mapPersonDocumentToPageModel(loyaltyProgramContactDocument, language)
        : {
            ...defaults.contactPerson,
            correspondenceLanguage: mapUserToCorrespondenceLanguage(language),
          },
    adminContactPerson: mapPersonDocumentToPageModel(document.admin, language),
    partnerPlusBenefitGeneralDto: {
      companyPosition:
        (isThereExistingPartnerPlus &&
          partnerPlusBenefitDocument.companyPosition) ||
        null,
      companyDepartment:
        (isThereExistingPartnerPlus &&
          partnerPlusBenefitDocument.companyDepartment) ||
        null,
      receiveInfoMail:
        (isThereExistingPartnerPlus &&
          partnerPlusBenefitDocument.receiveInfoMail) ||
        defaults.partnerPlusBenefitGeneralDto.receiveInfoMail,
      sendNewsletters:
        (isThereExistingPartnerPlus &&
          partnerPlusBenefitDocument.subscribeToNewsletter) ||
        defaults.partnerPlusBenefitGeneralDto.sendNewsletters,
      companyTravelersCount: ((isThereExistingPartnerPlus &&
        partnerPlusBenefitDocument.travelersCount) ||
        defaults.partnerPlusBenefitGeneralDto
          .companyTravelersCount) as CompanyTravellersCount,
      expectedAnnualFlightSpending: ((isThereExistingPartnerPlus &&
        partnerPlusBenefitDocument.annualFlightSpending) ||
        defaults.partnerPlusBenefitGeneralDto
          .expectedAnnualFlightSpending) as AnnualFlightSpending,
    },
  };
};

const mapPageModelToRequest = (
  data: BasicAccountBenefitsModel,
  basicAccountApplicationRef: string,
  productRef: string,
): BasicAccountBenefitsDto => {
  const contact =
    data.isContactPersonSame === "yes"
      ? data.adminContactPerson
      : data.contactPerson;

  const hasNoLoyalties = data.hasLoyalties === "no";
  return {
    basicAccountApplicationRef,
    productRef,
    customizedCard: data.customizedCard,
    isCashWithdrawalPossible: mapYesNoStringToBoolean(data.hasCashOnCard),
    partnerPlusBenefitsDto: {
      username: hasNoLoyalties ? null : data.plusPartnerUsername,
      general: hasNoLoyalties
        ? {
            receiveInfoMail: data.partnerPlusBenefitGeneralDto.receiveInfoMail,
            subscribeToNewsletter:
              data.partnerPlusBenefitGeneralDto.sendNewsletters,
            companyDepartment:
              data.partnerPlusBenefitGeneralDto.companyDepartment,
            companyPosition: data.partnerPlusBenefitGeneralDto.companyPosition,
            expectedAnnualFlightSpending:
              data.partnerPlusBenefitGeneralDto.expectedAnnualFlightSpending,
            companyTravelersCount:
              data.partnerPlusBenefitGeneralDto.companyTravelersCount,
          }
        : null,
      contact: hasNoLoyalties
        ? {
            gender: contact.gender as Gender,
            firstName: contact.firstName,
            lastName: contact.lastName,
            mobile: mapPhonePageModelToRequest(contact.mobile),
            emailBusiness: contact.emailBusiness,
            correspondenceLanguage:
              contact.correspondenceLanguage as LanguageBackendType,
            isSameAsAdmin: data.isContactPersonSame === "yes",
          }
        : null,
    },
  };
};

const BasicAccountBenefitsMapper = {
  mapPageModelToRequest,
  mapDocumentToPageModel,
};

export default BasicAccountBenefitsMapper;
